// src/crypto/media.js
//
// 🟢 MEDIA DECRYPTION — browser twin of the mobile secretbox media path.
// Media bytes are symmetric-encrypted with nacl.secretbox (key `k`, nonce `n`),
// uploaded as ciphertext to R2/CDN. The symmetric key JSON is sealed per-device
// (see e2ee.unsealMediaKey). Here we fetch the ciphertext, open it, and hand back
// a blob object URL the <img>/<video>/<audio> tags can use directly.
//
// 🟥 AND THEY USED TO LIVE ONLY IN THIS PROCESS. A module-global Map of 120
//    object URLs, shared across every chat, evicted first-in-first-out — a cache
//    hit returned early and never touched the order, so recency changed nothing
//    — and eviction REVOKED the url. A media-heavy chat resolves several hundred
//    blobs, so the photo at the top of the conversation you were reading had its
//    handle revoked out from under a live <img>, which has no onError and so
//    stayed a broken glyph for the life of the tab. That is "always broken".
//
// 🟢 THE BYTES ARE ON DISK NOW (services/mediaStore.js) and the object URL is a
//    handle over them. That is what makes revoking safe — anything still
//    rendering can ask for another handle — and it is what makes a RELOAD paint
//    instead of re-download, which is what "the web does not save the session"
//    was about.

import nacl from 'tweetnacl';
import util from 'tweetnacl-util';
import * as store from '../services/mediaStore';
import {
  classifyFailure, retryDelay, canRetry, MEDIA_ATTEMPTS, GONE, TRANSIENT, BROKEN,
} from '../services/mediaPolicy';

export { canRetry, GONE, TRANSIENT, BROKEN };

/**
 * The key a decrypted object is filed under — the ciphertext url plus the key
 * that opens it, so a re-keyed message is a different entry rather than a stale
 * hit that will not open.
 */
/* 🔎 Set window.__ALLO_MEDIA_LOG = true in the console to trace every tile:
   live hit / store hit / store miss / fetch result. Off by default — a chat with
   forty photos would otherwise print forty lines per render. Failures and a
   locked vault are ALWAYS logged, because those are the ones being hunted. */
const MEDIA_LOG = typeof window !== 'undefined' && !!window.__ALLO_MEDIA_LOG;

export const mediaKeyFor = (url, kB64) => `${url}|${kB64}`;

/** Re-mint a handle for something already decrypted. Used by onError healing. */
export const remintMedia = (url, kB64) => store.mintFromStore(mediaKeyFor(url, kB64));

/**
 * A handle for something we ALREADY HAVE — live, or on disk from an earlier
 * session. Never touches the network.
 *
 * 🟥 "LOAD VIDEO" IS A QUESTION ABOUT A DOWNLOAD, AND IT WAS BEING ASKED ABOUT
 *    BYTES ALREADY IN HAND. A clip is not auto-fetched because it is tens of
 *    megabytes through the wire and through secretbox before one frame exists —
 *    that is the whole reason for the button. Once those bytes are stored, none
 *    of that is true any more, and asking again is asking somebody to authorise
 *    work that is not going to happen.
 */
export async function cachedMediaUrl(url, kB64) {
  if (!url) return null;
  const key = mediaKeyFor(url, kB64);
  return store.liveUrl(key) || await store.mintFromStore(key);
}

/**
 * Why the last attempt on this url failed, for the caller that has to decide
 * whether to offer a button. `gone` means the bucket's lifecycle rule deleted
 * the object and no retry can succeed; `transient` means try again.
 */
const _why = new Map();
export const mediaFailure = (url, kB64) => _why.get(mediaKeyFor(url, kB64)) || null;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Fetch + decrypt one encrypted media URL.
 *
 * 🟢 THREE PLACES TO LOOK BEFORE THE NETWORK: a handle already minted, the
 *    bytes on disk from an earlier session, and only then the bucket. The
 *    middle one is the whole point — it is what makes a reload paint instead of
 *    re-download.
 *
 * @param {string} url         ciphertext URL (R2/CDN)
 * @param {string} kB64        symmetric key (base64)
 * @param {string} nB64        symmetric nonce (base64)
 * @param {string} mime        mime type for the resulting Blob
 * @returns {Promise<string|null>} object URL, or null on failure
 */
export async function decryptMediaUrl(url, kB64, nB64, mime = 'application/octet-stream') {
  if (!url) return null;
  const cacheKey = mediaKeyFor(url, kB64);

  const live = store.liveUrl(cacheKey);
  if (live) { MEDIA_LOG && console.warn('[media] live hit', url); return live; }
  /* 🟥 A LOCKED VAULT USED TO REJECT THE WHOLE ALBUM. mediaStore.mintFromStore
     THROWS VaultLocked on purpose — "a locked vault is not a missing file" — and
     nothing on this path caught it. decryptMessage maps an album through
     Promise.all, so one locked tile rejected the batch, `_mediaBatch` was never
     set, and every tile fell back to a spinner while the first still drew its
     inline blur. That is exactly "the first is broken and the rest just load".
     🟢 Locked is a reason to skip the CACHE, not a reason to fail: the media key
        rides on the message, so the bytes can still be fetched and decrypted. */
  let kept = null;
  try {
    kept = await store.mintFromStore(cacheKey);
  } catch (e) {
    if (e?.name !== 'VaultLocked') throw e;
    console.warn('[media] vault LOCKED — cache unavailable, fetching instead:', url);
  }
  if (kept) { _why.delete(cacheKey); MEDIA_LOG && console.warn('[media] store hit', url); return kept; }
  MEDIA_LOG && console.warn('[media] store miss → fetching', url);

  /* 🟥 ONE ATTEMPT, NO BACKOFF, WAS THE WHOLE RETRY STORY. A reload asks for
     every visible message at once, three lanes deep — and a burst like that
     against a CDN produces exactly the reported symptom: sometimes they show,
     sometimes they are broken, with no pattern to it and no way back except
     another reload. */
  for (let attempt = 1; attempt <= MEDIA_ATTEMPTS; attempt += 1) {
    if (attempt > 1) await sleep(retryDelay(attempt));
    let res;
    try {
      res = await fetch(url);
    } catch (netErr) {
      /* 🟥 A CROSS-ORIGIN REJECTION NEVER REACHES JAVASCRIPT AS A RESPONSE. The
         browser hands `fetch` an opaque TypeError with no status, which used to
         vanish into a bare `catch { return null }` — and it is the single most
         common cause of "blurred thumbnail, spinner forever": a bucket with no
         CORS policy. Named out loud, and classified as worth retrying, because
         a dropped connection arrives here identically. */
      _why.set(cacheKey, TRANSIENT);
      if (attempt === MEDIA_ATTEMPTS) {
        console.warn(
          '[media] fetch BLOCKED for', url,
          '\n  → almost always missing R2 bucket CORS (allow GET from this origin).',
          '\n  → see R2_SETUP.md §6.', netErr,
        );
        return null;
      }
      continue;
    }
    if (!res.ok) {
      const kind = classifyFailure(res.status);
      _why.set(cacheKey, kind);
      /* 🟥 A DELETED OBJECT IS NOT A FLAKY ONE. R2's lifecycle rule removes chat
         media by age, so a 404 is final — retrying it three times costs the
         reader three round trips to be told the same thing, and offering them a
         button afterwards is a promise the product cannot keep. */
      if (kind === GONE) {
        console.warn(`[media] ${res.status} for ${url} — the object has been deleted or expired`);
        return null;
      }
      if (attempt === MEDIA_ATTEMPTS) {
        console.warn(`[media] fetch ${res.status} for ${url} — giving up after ${attempt} attempts`);
        return null;
      }
      continue;
    }

    const cipher = new Uint8Array(await res.arrayBuffer());
    const opened = nacl.secretbox.open(cipher, util.decodeBase64(nB64), util.decodeBase64(kB64));
    if (!opened) {
      /* 🟥 NOT A RETRY. The bytes arrived and would not open: the key is wrong,
         or the object is truncated. Asking for them again produces the same
         bytes and the same failure, more slowly. */
      _why.set(cacheKey, BROKEN);
      console.warn('[media] secretbox.open FAILED for', url, '— wrong key/nonce or truncated bytes');
      return null;
    }
    /* 🟥 THE BYTES OUTRANK THE LABEL. An R2 object key has no extension, so a
       caller with no explicit mime can only fall back on `kind` — and for audio
       that fallback is a flat 'audio/mp4'. A browser recording is usually
       WebM/Opus, so the Blob got labelled as something it is not: the element
       SNIFFED the container and played it, while its own controls showed a load
       error over audible sound. Maddening, and invisible in any log.

       Reading the magic bytes fixes it for messages ALREADY SENT, which a new
       field on the document cannot do — there is nothing to migrate and no
       re-send to ask anyone for. */
    _why.delete(cacheKey);
    return store.keep(cacheKey, new Blob([opened], { type: sniffMime(opened, mime) }));
  }
  return null;
}

/** Plain (unencrypted) media just passes through — some legacy/self messages aren't sealed. */
export function passthroughUrl(url) {
  return url || null;
}

/**
 * What the first few bytes say this actually is.
 *
 * 🟢 ONLY OVERRIDES WHEN IT IS SURE. An unrecognised header returns the caller's
 *    mime untouched — a wrong guess replaced by a different wrong guess would be
 *    no improvement, and every format not listed here was already working.
 */
export function sniffMime(bytes, fallback = 'application/octet-stream') {
  try {
    const b = bytes;
    if (!b || b.length < 12) return fallback;
    const ascii = (o, n) => String.fromCharCode(...b.slice(o, o + n));

    // container formats
    if (b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) {
      // EBML — WebM or Matroska. Audio-only recordings are the common case here,
      // so honour what the caller expected when it also said "audio".
      return String(fallback).startsWith('audio') ? 'audio/webm' : 'video/webm';
    }
    if (ascii(4, 4) === 'ftyp') {
      return String(fallback).startsWith('audio') ? 'audio/mp4' : 'video/mp4';
    }
    if (ascii(0, 4) === 'OggS') return String(fallback).startsWith('video') ? 'video/ogg' : 'audio/ogg';
    if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WAVE') return 'audio/wav';
    if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) return 'audio/mpeg';        // ID3
    if (b[0] === 0xff && (b[1] & 0xe0) === 0xe0) return 'audio/mpeg';                // raw MP3 frame

    // images
    if (b[0] === 0x89 && ascii(1, 3) === 'PNG') return 'image/png';
    if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
    if (ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') return 'image/webp';
    if (ascii(0, 3) === 'GIF') return 'image/gif';
    if (ascii(0, 4) === '%PDF') return 'application/pdf';

    return fallback;
  } catch {
    return fallback;
  }
}

export function guessMime(nameOrUrl = '', kind = 'image') {
  const s = String(nameOrUrl).toLowerCase();
  if (kind === 'video' || /\.(mp4|mov|webm|m4v)/.test(s)) return 'video/mp4';
  if (kind === 'audio' || /\.(m4a|mp3|aac|ogg|wav)/.test(s)) return 'audio/mp4';
  if (/\.png/.test(s)) return 'image/png';
  if (/\.webp/.test(s)) return 'image/webp';
  if (/\.gif/.test(s)) return 'image/gif';
  if (kind === 'image' || /\.(jpe?g)/.test(s)) return 'image/jpeg';
  return 'application/octet-stream';
}
